import { useState } from 'react'
import { Pencil, Plus, Rows3, Wallet } from 'lucide-react'
import { DateValue, EmptyState, ErrorState, FormDialog, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { usePermission } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import { todayLocal } from '@/lib/dates'
import {
  ELEMENT_TYPE_LABELS, useAssignEmployeePayElement, useEmployeePayElements, usePayElements,
} from '@/features/payroll'
import type { ElementType, EmployeePayElementView } from '@/features/payroll'
import { EditSalaryDialog } from './EditSalaryDialog'

interface PayDraft {
  elementId: string
  /** Fixed once chosen when changing an existing line — changing it is a different line. */
  lockedElement: boolean
  amount: string
  currency: string
  effectiveFrom: string
  notes: string
}

/**
 * What this person is paid, element by element.
 *
 * <p>The amounts existed and had an endpoint, and no screen showed them: the only way to see
 * somebody's basic was to run a payroll and read the payslip. Shown here to whoever may read
 * payroll, current amounts first, ended ones underneath so a figure from last year can still be
 * explained.
 *
 * <p>Changing an amount here is the <b>direct</b> path — written at once and recorded in the
 * access log, for opening figures and corrections. A rise somebody has to approve still goes
 * through Salary revisions, and the helper text says so, because the two buttons would otherwise
 * look like alternatives.
 *
 * <p>"Edit salary" is the same direct path for the whole package at once — every element, one
 * date, one save. The pencil on a line stays for the quick fix to one figure.
 */
export function PayTab({ employeeId }: { employeeId: string }) {
  const pay = useEmployeePayElements(employeeId)
  const canWrite = usePermission('payroll:write')
  const elements = usePayElements()
  const assign = useAssignEmployeePayElement(employeeId)

  const [draft, setDraft] = useState<PayDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingSalary, setEditingSalary] = useState(false)

  const today = todayLocal()
  const isCurrent = (line: EmployeePayElementView) =>
    line.effectiveFrom <= today && (line.effectiveTo == null || line.effectiveTo >= today)
  const lines = [...(pay.data ?? [])].sort((a, b) =>
    Number(isCurrent(b)) - Number(isCurrent(a)) || b.effectiveFrom.localeCompare(a.effectiveFrom))

  // Informational elements are never paid, and retired ones are not offered on new packages:
  // the server refuses neither from this path, so the list is where the choice is narrowed.
  const choosable = (elements.data ?? []).filter(
    (element) => element.active && element.elementType !== 'INFORMATIONAL')

  function open(line?: EmployeePayElementView) {
    setError(null)
    setDraft(line
      ? {
          elementId: line.elementId,
          lockedElement: true,
          amount: line.amount == null ? '' : String(line.amount),
          currency: line.currency ?? '',
          effectiveFrom: today,
          notes: '',
        }
      : {
          elementId: '', lockedElement: false, amount: '', currency: '',
          effectiveFrom: today, notes: '',
        })
  }

  async function submit() {
    if (!draft) return
    setError(null)
    const amount = Number(draft.amount)
    if (!draft.elementId) { setError('Choose a pay element.'); return }
    if (!draft.amount || !Number.isFinite(amount) || amount <= 0) {
      setError('Enter an amount more than zero.')
      return
    }
    try {
      await assign.mutateAsync({
        elementId: draft.elementId,
        effectiveFrom: draft.effectiveFrom,
        amount,
        currency: draft.currency.trim() ? draft.currency.trim().toUpperCase() : null,
        notes: draft.notes.trim() || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That amount could not be saved.')
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <Wallet className="size-4" aria-hidden="true" />
          Pay elements
        </h3>
        {canWrite && (
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => open()}>
              <Plus className="size-4" aria-hidden="true" />
              Add pay element
            </Button>
            <Button
              type="button" size="sm" disabled={!pay.isSuccess || !elements.data}
              onClick={() => setEditingSalary(true)}
            >
              <Rows3 className="size-4" aria-hidden="true" />
              Edit salary
            </Button>
          </div>
        )}
      </div>
      <p className="mb-3 text-sm text-[var(--color-muted)]">
        Changes here take effect directly and are recorded in the access log — use them for
        opening figures and corrections. A pay rise that needs approval goes through
        Compensation › Salary revisions.
      </p>

      {pay.isPending && <Skeleton className="h-24" />}
      {pay.isError && <ErrorState error={pay.error} onRetry={() => void pay.refetch()} />}
      {pay.isSuccess && lines.length === 0 && (
        <EmptyState
          title="Nothing on file"
          description="Until this person has a pay element, a payroll run pays them nothing."
        />
      )}

      {pay.isSuccess && lines.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-start text-xs text-[var(--color-muted)]">
              <tr>
                <th className="p-2 text-start font-medium">Element</th>
                <th className="p-2 text-start font-medium">Kind</th>
                <th className="p-2 text-end font-medium">Amount</th>
                <th className="p-2 text-start font-medium">Currency</th>
                <th className="p-2 text-start font-medium">From</th>
                <th className="p-2 text-start font-medium">To</th>
                {canWrite && <th className="p-2 text-end font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const current = isCurrent(line)
                return (
                  <tr
                    key={line.id}
                    className={`border-t border-[var(--color-border)] ${current ? '' : 'text-[var(--color-muted)]'}`}
                  >
                    <td className="p-2">
                      <span className={current ? 'text-[var(--color-heading)]' : ''}>
                        {line.elementName ?? line.elementCode ?? '—'}
                      </span>
                      {line.elementCode && (
                        <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">
                          {line.elementCode}
                        </span>
                      )}
                      {/* "Opening salary (import)" is how a brought-forward figure is told
                          apart from one changed here or by a revision. */}
                      {line.notes && (
                        <span className="block text-xs text-[var(--color-muted)]">{line.notes}</span>
                      )}
                    </td>
                    <td className="p-2">
                      {line.elementType
                        ? ELEMENT_TYPE_LABELS[line.elementType as ElementType] ?? line.elementType
                        : '—'}
                    </td>
                    <td className="p-2 text-end tabular-nums">
                      {line.amount ?? (line.percentage != null ? `${line.percentage}%` : '—')}
                    </td>
                    <td className="p-2">{line.currency ?? '—'}</td>
                    <td className="p-2"><DateValue value={line.effectiveFrom} /></td>
                    <td className="p-2">
                      {line.effectiveTo
                        ? <DateValue value={line.effectiveTo} />
                        : <StatusBadge status="OPEN" tone="success" label="Open" />}
                    </td>
                    {canWrite && (
                      <td className="p-2 text-end">
                        {current && (
                          <Button
                            type="button" variant="ghost" size="sm"
                            aria-label={`Change ${line.elementName ?? line.elementCode ?? 'element'}`}
                            onClick={() => open(line)}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mounted only while open, so every opening starts from what is paid at that moment. */}
      {editingSalary && (
        <EditSalaryDialog
          employeeId={employeeId}
          open
          onOpenChange={setEditingSalary}
          elements={elements.data ?? []}
          lines={pay.data ?? []}
        />
      )}

      <FormDialog
        open={draft !== null}
        onOpenChange={(next) => { if (!next) setDraft(null) }}
        title={draft?.lockedElement ? 'Change the amount' : 'Add a pay element'}
        description="Takes effect from the date below. An earlier amount for the same element is closed the day before; the same date corrects it."
        submitLabel="Save"
        loading={assign.isPending}
        error={error}
        onSubmit={() => { void submit() }}
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="pay-element">Pay element</Label>
              <Select
                id="pay-element"
                className="w-full"
                disabled={draft.lockedElement}
                value={draft.elementId}
                onChange={(event) => setDraft({ ...draft, elementId: event.target.value })}
              >
                <option value="">Choose…</option>
                {(draft.lockedElement ? elements.data ?? [] : choosable).map((element) => (
                  <option key={element.id} value={element.id}>
                    {element.name} ({element.code})
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="pay-amount">Amount</Label>
              <Input
                id="pay-amount"
                type="number"
                min="0"
                step="0.01"
                value={draft.amount}
                onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="pay-currency">Currency</Label>
              <Input
                id="pay-currency"
                maxLength={3}
                placeholder="AED"
                value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="pay-from">Effective from</Label>
              <DatePicker
                id="pay-from"
                value={draft.effectiveFrom}
                onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="pay-notes">Notes</Label>
              <Input
                id="pay-notes"
                value={draft.notes}
                placeholder="Why it changed"
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </Card>
  )
}
